import { CSSProperties, useCallback, useMemo, useRef, useState } from 'react';

import { useJobQuoteContext } from 'components/JobQuote/context';

export interface JobQuoteCompactHook {
  collapseRef: React.MutableRefObject<HTMLDivElement | null>;
  open: boolean;
  onClick: () => void;
  contentStyle: CSSProperties;
  isLoading: boolean;
  disabled: boolean;
}

export default function useJobQuoteCompact(
  milliseconds = 300,
): JobQuoteCompactHook {
  const { isLoading } = useJobQuoteContext();
  const [open, setOpen] = useState<boolean>(false);
  const [disabled, setDisabled] = useState<boolean>(false);
  const [maxHeight, setMaxHeight] = useState<string>('0px');
  const collapseRef = useRef<HTMLDivElement | null>(null);

  const handleDisable = useCallback(() => {
    setDisabled(true);
    setTimeout(() => setDisabled(false), milliseconds);
  }, [milliseconds]);

  const onClick = useCallback(() => {
    const clientHeight = `${collapseRef.current?.clientHeight || 100}px`;
    if (disabled || isLoading) return;
    handleDisable();
    if (!open) {
      setOpen(true);
      setTimeout(() => {
        setMaxHeight(clientHeight);
      }, 10);
      setTimeout(() => {
        setMaxHeight('none');
      }, milliseconds + 100);
      return;
    }
    setOpen(false);
    setMaxHeight(clientHeight);
    setTimeout(() => setMaxHeight('0px'), 10);
  }, [handleDisable, open, disabled, milliseconds, isLoading]);

  const style = useMemo(
    () => ({
      maxHeight,
      transitionDuration: `${milliseconds}ms`,
    }),
    [maxHeight, milliseconds],
  );

  return {
    collapseRef,
    open,
    onClick,
    disabled,
    isLoading,
    contentStyle: style,
  };
}
